import type { Object3D } from 'three';
import type {
	SketchEntityReferenceRebuildServiceFactoryDependencies,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildServiceFactory';
import { SketchEntityReferenceSourceObjectResolver } from '../source/projection/SketchEntityReferenceSourceObjectResolver';
import { SketchEntityReferenceProjectionService } from '../source/projection/SketchEntityReferenceProjectionService';
import { RebuildSketchEntityReferencesService } from './RebuildSketchEntityReferencesService';
import { SketchEntityReferenceProjectionMutationDispatcher } from './SketchEntityReferenceProjectionMutationDispatcher';
import { SketchEntityReferenceRebuildExecutor } from './SketchEntityReferenceRebuildExecutor';
import { SketchReferenceEntityFactory } from '../target/SketchReferenceEntityFactory';
import { SketchReferenceRelationAttachDispatcher } from '../relation/SketchReferenceRelationAttachDispatcher';
import { SketchEntityReferenceObjectCleanupService } from '../target/SketchEntityReferenceObjectCleanupService';
import { CreateGeometryReferencesCommandService } from '../command/create/CreateGeometryReferencesCommandService';
import { RemoveGeometryReferencesService } from '../lifecycle/RemoveGeometryReferencesService';
import { TopologyBoundaryReferenceGroupReconciler } from './TopologyBoundaryReferenceGroupReconciler';
import type {
	TopologyEdgeSourceDescriptor,
} from '../model/ExternalGeometrySourceDescriptor';
import type {
	SketchEntityReferenceTag,
} from '../model/SketchEntityReferenceIds';

export type {
	SketchEntityReferenceRebuildServiceFactoryDependencies,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildServiceFactory';

/**
 * 转换实体引用 Rebuild 模块装配工厂。
 *
 * 说明：
 * 1. 本类集中创建 Projection、SourceObjectResolver、MutationDispatcher 和 Executor；
 * 2. 对外只返回职责收窄后的批次 RebuildService；
 * 3. 低层装配变化不再扩散到批次服务、web-client 或测试调用方。
 */
export class SketchEntityReferenceRebuildServiceFactory {
	/**
	 * 创建转换实体引用批次重建服务。
	 *
	 * @param dependencies 文档运行时、仓储及可选替换能力。
	 * @returns 已完成内部装配的批次重建服务。
	 */
	public create(dependencies: SketchEntityReferenceRebuildServiceFactoryDependencies): RebuildSketchEntityReferencesService {
		const projectionService =
			dependencies.projectionService ??
			new SketchEntityReferenceProjectionService({
				runtime: dependencies.runtime,
				sourceResolver: dependencies.sourceResolver,
				lineProjectionPreprocessor: dependencies.lineProjectionPreprocessor,
				circleProjectionPreprocessor: dependencies.circleProjectionPreprocessor,
				arcProjectionPreprocessor: dependencies.arcProjectionPreprocessor,
				ellipseProjectionPreprocessor: dependencies.ellipseProjectionPreprocessor,
				ellipseArcProjectionPreprocessor: dependencies.ellipseArcProjectionPreprocessor,
				parabolaProjectionPreprocessor: dependencies.parabolaProjectionPreprocessor,
				splineProjectionPreprocessor: dependencies.splineProjectionPreprocessor,
				styleSplineProjectionPreprocessor: dependencies.styleSplineProjectionPreprocessor,
			});
		const sourceObjectResolver =
			dependencies.sourceObjectResolver ??
			new SketchEntityReferenceSourceObjectResolver({
				runtime: dependencies.runtime,
				topologyEdgeRelocationService: dependencies.topologyEdgeRelocationService,
			});
		const objectCleanupService = dependencies.objectCleanupService
			?? new SketchEntityReferenceObjectCleanupService();
		const entityFactory = dependencies.entityFactory
			?? new SketchReferenceEntityFactory({
				...dependencies,
				objectCleanupService,
			});
		const relationAttachDispatcher = dependencies.relationAttachDispatcher
			?? new SketchReferenceRelationAttachDispatcher(dependencies);
		const rebuildExecutor = new SketchEntityReferenceRebuildExecutor({
			...dependencies,
			projectionService,
			sourceObjectResolver,
			entityFactory,
			relationAttachDispatcher,
			objectCleanupService,
			projectionMutationDispatcher: new SketchEntityReferenceProjectionMutationDispatcher(),
		});
		const createService = dependencies.createService ?? (() => {
			const createRuntime = {
				getSketchGroup: (sketchId: string) => dependencies.runtime.getSketchGroup(sketchId),
				getSketchObject: (sketchId: string, entityId: string) => (
					dependencies.runtime.getSketchObject(sketchId, entityId)
				),
				getFeatureLine: (owningObjectId: string, featureEdgeIndex: number) => (
					dependencies.runtime.getFeatureLine(owningObjectId, featureEdgeIndex)
				),
				resolveTopologyOwnerHost: dependencies.runtime.resolveTopologyOwnerHost
					? (descriptor: TopologyEdgeSourceDescriptor) => (
						dependencies.runtime.resolveTopologyOwnerHost?.(descriptor) ?? null
					)
					: undefined,
				writeReferenceTag: (
					targetObject: Object3D,
					tag: SketchEntityReferenceTag,
				) => dependencies.runtime.writeReferenceTag?.(targetObject, tag.referenceId),
			};
			return new CreateGeometryReferencesCommandService({
				...dependencies,
				runtime: createRuntime,
				repository: dependencies.repository,
				solveTargetSketchParametricState: dependencies.solveTargetSketchParametricState,
				projectionService,
				entityFactory,
				relationAttachDispatcher,
				objectCleanupService,
				basicConstraintRestoreService: dependencies.basicConstraintRestoreService,
			});
		})();
		const removeService = dependencies.removeService ?? new RemoveGeometryReferencesService({
			repository: dependencies.repository,
			relationAttachDispatcher,
			runtime: dependencies.runtime,
			objectCleanupService,
			runDefinitionChangeScope: (targetSketchId, ownerId, work) => {
				const targetSketch = dependencies.runtime.getSketchGroup(targetSketchId);
				return targetSketch
					? targetSketch.runDefinitionChangeOwnerScope(ownerId, work)
					: work();
			},
		});
		const topologyBoundaryGroupReconciler = new TopologyBoundaryReferenceGroupReconciler({
			runtime: dependencies.runtime,
			createService,
			removeService,
			repository: dependencies.repository,
		});

		return new RebuildSketchEntityReferencesService({
			runtime: dependencies.runtime,
			repository: dependencies.repository,
			rebuildExecutor,
			sourceObjectResolver,
			basicConstraintRestoreService: dependencies.basicConstraintRestoreService,
			targetAnchorBindingCollector: dependencies.targetAnchorBindingCollector,
			targetChangeCommitter: dependencies.targetChangeCommitter,
			topologyBoundaryGroupReconciler,
			solveTargetSketchParametricState: dependencies.solveTargetSketchParametricState,
		});
	}
}
